import { Box, Typography } from "@mui/material";

import adFontUrl from "@/assets/fonts/roboto-condensed-subset.woff2";

import { AD_ROOT_CLASS, AdFont, installAdRootStyles } from "./adRoot";
import TemplateBox from "./components/TemplateBox";
import TemplateCTA from "./components/TemplateCTA";
import TemplateImage from "./components/TemplateImage";
import TemplateList from "./components/TemplateList";
import TemplateOverlayAnchorBox from "./components/TemplateOverlayAnchorBox";
import TemplateTextBox from "./components/TemplateTextBox";
import {
  Component,
  ComponentMapping,
  Fields,
  Template,
} from "./template.interface";

// The served ad's font and resets, registered once per document (see `adRoot.ts`).
installAdRootStyles(adFontUrl);

const MAPPING: ComponentMapping = {
  overlayAnchorBox: TemplateOverlayAnchorBox,
  box: TemplateBox,
  textBox: TemplateTextBox,
  list: TemplateList,
  cta: TemplateCTA,
  image: TemplateImage,
};

function componentMapper(
  data: Component[],
  values: any,
  fields: Fields,
  isEdit: boolean,
  tplVariantId?: number | string
) {
  return data.map((component, index) => {
    if (component) {
      const { components = null, id, ...rest } = component;
      const MyComponent = MAPPING[id];
      if (!MyComponent) return null;
      if (components) {
        return (
          <MyComponent
            id={id}
            {...rest}
            key={`${index}${id}`}
            values={values}
            fields={fields}
            isEdit={isEdit}
            tplVariantId={tplVariantId}
          >
            {componentMapper(components, values, fields, isEdit, tplVariantId)}
          </MyComponent>
        );
      }
      return (
        <MyComponent
          id={id}
          {...rest}
          key={`${index}${id}`}
          values={values}
          fields={fields}
          isEdit={isEdit}
          tplVariantId={tplVariantId}
        />
      );
    }
    return null;
  });
}

export default function DisplayTemplate({
  values,
  template,
  isEdit = false,
  isOverlay = true,
  width,
  height,
  isLabel = false,
  variantId,
  measureRef,
  adFont,
}: {
  values: Record<string, any>;
  template: Template;
  isEdit?: boolean;
  isOverlay?: boolean;
  width: number;
  height: number;
  isLabel?: boolean;
  /** Stamped onto every measurable node as `data-tpl-variant`. */
  variantId?: number | string;
  /** The intrinsic-size box, handed to the offscreen measurement rig. */
  measureRef?: React.Ref<HTMLDivElement>;
  /**
   * The type environment to render in (see `AdFont`). **Required, with no default**, so every caller
   * has to decide and `tsc` catches one that forgets: a silent default would put a new-tag creative
   * in the old look (or the reverse) with nothing visibly broken. The studio passes `served-v2`; a
   * stored creative passes `adFontOfTag(creative.creativeContentTag)`; `legacy` inherits the
   * dashboard's Poppins, as creatives made before the Roboto Condensed tag still serve.
   */
  adFont: AdFont;
}) {
  const { fields, definition } = template;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {isLabel && (
        <Box
          sx={{
            backgroundColor: "rgba(0, 0, 0, 0.1)",
            width: "max-content",
            padding: "4px 12px",
            borderRadius: "4px",
          }}
        >
          <Typography variant="captionReg">
            {width} x {height} px
          </Typography>
        </Box>
      )}
      {/*
        No border or shadow of its own: the ad starts at (0,0) and is exactly W x H, as in the served
        page. The old 1px border pushed the ad 1px in and, inside a W x H clip box, cut 3px off its
        right and bottom (render parity D9). The ad's own frame is the served `#mys-overlay`
        (`TemplateOverlayAnchorBox`); a host that wants more draws it outside (`adFrameSx`).
      */}
      <Box
        sx={{
          position: "relative",
          maxWidth: `${width}px`,
          maxHeight: `${height}px`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: width,
            height: height,
            backgroundColor: "rgba(0, 0, 0, 0)", // transparent overlay
            // Above the ad's own click layer (z 20, as served), so a shielded preview stays inert.
            zIndex: isOverlay ? 30 : -2,
          }}
        />
        {/* With `served-v2`, `tpl-ad-root` stands where the tag's `<ins>` stands: inside its font and resets. */}
        <div
          ref={measureRef}
          className={adFont === "served-v2" ? AD_ROOT_CLASS : undefined}
        >
          {componentMapper([definition], values, fields, isEdit, variantId)}
        </div>
      </Box>
    </Box>
  );
}
